import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { paymentApi, ReconciliationRecord } from '../../api/paymentApi';

interface ReconciliationDebtModalProps {
  isOpen: boolean;
  buildingId: string;
  row: ReconciliationRecord;
  onClose: () => void;
  onFeePayment: (apartmentId: string, apartmentCode: string) => void;
}

export const ReconciliationDebtModal: React.FC<ReconciliationDebtModalProps> = ({
  isOpen,
  buildingId,
  row,
  onClose,
  onFeePayment,
}) => {
  const [mode, setMode] = useState<'fee' | 'contract'>('fee');
  const [apartmentId, setApartmentId] = useState('');
  const [apartmentSearch, setApartmentSearch] = useState('');
  const [apartmentOptions, setApartmentOptions] = useState<
    Array<{ id: string; code: string; name?: string | null }>
  >([]);

  useEffect(() => {
    if (!isOpen) return;
    setMode('fee');
    paymentApi
      .getReconciliationActionContext(row.id, buildingId)
      .then((ctx) => {
        if (ctx.suggestedApartment) {
          setApartmentId(ctx.suggestedApartment.id);
          setApartmentSearch(ctx.suggestedApartment.code);
          setApartmentOptions([ctx.suggestedApartment]);
        } else {
          const code = row.apartmentCode || row.inferredApartmentCode;
          setApartmentSearch(code || '');
          setApartmentId('');
          setApartmentOptions([]);
        }
      })
      .catch(() => {
        const code = row.apartmentCode || row.inferredApartmentCode;
        setApartmentSearch(code || '');
        setApartmentId('');
        setApartmentOptions([]);
      });
  }, [isOpen, row.id, buildingId, row.apartmentCode, row.inferredApartmentCode]);

  useEffect(() => {
    if (!isOpen || !buildingId || apartmentSearch.trim().length < 2) return;
    const timer = setTimeout(async () => {
      try {
        const list = await paymentApi.searchApartmentMatches(buildingId, apartmentSearch.trim());
        setApartmentOptions(list);
        if (list.length === 1) {
          setApartmentId(list[0].id);
        }
      } catch {
        setApartmentOptions([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [apartmentSearch, buildingId, isOpen]);

  if (!isOpen) return null;

  const handleContinue = () => {
    if (mode !== 'fee') return;
    if (!apartmentId) {
      toast.error('Vui lòng chọn căn hộ');
      return;
    }
    const apt = apartmentOptions.find((a) => a.id === apartmentId);
    onFeePayment(apartmentId, apt?.code || apartmentSearch.trim());
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="border-b px-5 py-4">
          <h3 className="text-lg font-semibold text-gray-900">Gạch nợ</h3>
          <p className="text-sm text-gray-500 mt-1 truncate" title={row.description}>
            {row.description || '—'}
          </p>
        </div>

        <div className="px-5 py-4 space-y-3">
          <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
            <input
              type="radio"
              name="debt-mode"
              checked={mode === 'fee'}
              onChange={() => setMode('fee')}
              className="mt-1"
            />
            <div>
              <div className="font-medium text-gray-900">Thanh toán phí</div>
              <div className="text-sm text-gray-500">
                Gạch nợ phí dịch vụ như tab Công nợ (chọn căn hộ và hóa đơn).
              </div>
            </div>
          </label>

          {mode === 'fee' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Căn hộ</label>
              <input
                type="text"
                value={apartmentSearch}
                onChange={(e) => {
                  setApartmentSearch(e.target.value);
                  setApartmentId('');
                }}
                placeholder="Tìm theo mã căn hộ..."
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              />
              {apartmentOptions.length > 0 && (
                <ul className="mt-1 border border-gray-200 rounded-md max-h-32 overflow-y-auto">
                  {apartmentOptions.map((apt) => (
                    <li key={apt.id}>
                      <button
                        type="button"
                        onClick={() => {
                          setApartmentId(apt.id);
                          setApartmentSearch(apt.code);
                        }}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-cyan-50 ${
                          apartmentId === apt.id ? 'bg-cyan-50 text-cyan-800 font-medium' : ''
                        }`}
                      >
                        {apt.code}
                        {apt.name ? ` — ${apt.name}` : ''}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <label className="flex items-start gap-3 p-3 border rounded-lg opacity-60 cursor-not-allowed">
            <input
              type="radio"
              name="debt-mode"
              checked={mode === 'contract'}
              onChange={() => setMode('contract')}
              disabled
              className="mt-1"
            />
            <div>
              <div className="font-medium text-gray-900">Thanh toán hợp đồng thu</div>
              <div className="text-sm text-gray-500">Sẽ triển khai sau.</div>
            </div>
          </label>
        </div>

        <div className="border-t px-5 py-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Đóng
          </button>
          <button
            type="button"
            onClick={handleContinue}
            disabled={mode !== 'fee'}
            className="px-4 py-2 text-sm bg-cyan-600 text-white rounded-md hover:bg-cyan-700 disabled:opacity-60"
          >
            Tiếp tục
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReconciliationDebtModal;
